// ===================================
// PROFILE PAGE - src/pages/Profile.tsx
// Updated with Agentic Access Code Management
// ===================================
import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import Header from "../components/Header";
import { 
  User, 
  Mail, 
  Building, 
  Shield, 
  Settings, 
  Save,
  Lock,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  Clock
} from "lucide-react";
import { notifySuccess, notifyError } from "../utils/Toastify";
import api from "../services/api";

// 🔐 NEW: Interface for Agentic Code Info
interface AgenticCodeInfo {
  hasCode: boolean;
  setAt?: string;
  lastChanged?: string;
  hasActiveSession: boolean;
  sessionExpiresAt?: string;
  isLocked: boolean;
  lockedUntil?: string;
  failedAttempts: number;
}

const Profile: React.FC = () => {
  const { user, updateProfile } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
  });

  // ===================================
  // 🔐 NEW: AGENTIC CODE STATE
  // ===================================
  const [agenticCodeInfo, setAgenticCodeInfo] = useState<AgenticCodeInfo | null>(null);
  const [showAgenticForm, setShowAgenticForm] = useState(false);
  const [agenticFormData, setAgenticFormData] = useState({
    currentCode: "",
    newCode: "",
    confirmCode: ""
  });
  const [showCodes, setShowCodes] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isSubmittingCode, setIsSubmittingCode] = useState(false);
  const [loadingCodeInfo, setLoadingCodeInfo] = useState(true);

  // ===================================
  // 🔐 NEW: FETCH AGENTIC CODE INFO
  // ===================================
  useEffect(() => {
    fetchAgenticCodeInfo();
  }, []);

  const fetchAgenticCodeInfo = async () => {
    try {
      setLoadingCodeInfo(true);
      const response = await api.get("/user/agentic-code/info");
      
      if (response.data.success) {
        setAgenticCodeInfo(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching agentic code info:", error);
      notifyError("Failed to load agentic code information");
    } finally {
      setLoadingCodeInfo(false);
    }
  };

  // ===================================
  // 🔐 NEW: HANDLE AGENTIC CODE SUBMISSION
  // ===================================
  const handleAgenticCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (agenticFormData.newCode.length < 6) {
      notifyError("Code must be at least 6 digits");
      return;
    }

    if (!/^\d+$/.test(agenticFormData.newCode)) {
      notifyError("Code must contain only numbers");
      return;
    }

    if (agenticFormData.newCode !== agenticFormData.confirmCode) {
      notifyError("Codes do not match");
      return;
    }

    if (agenticCodeInfo?.hasCode && !agenticFormData.currentCode) {
      notifyError("Current code is required to update");
      return;
    }

    try {
      setIsSubmittingCode(true);

      const response = await api.post("/user/agentic-code/set", {
        currentCode: agenticFormData.currentCode || undefined,
        newCode: agenticFormData.newCode,
        confirmCode: agenticFormData.confirmCode
      });

      if (response.data.success) {
        notifySuccess(
          agenticCodeInfo?.hasCode 
            ? "Agentic code updated successfully!" 
            : "Agentic code set successfully!"
        );
        
        // Reset form
        setAgenticFormData({
          currentCode: "",
          newCode: "",
          confirmCode: ""
        });
        setShowAgenticForm(false);
        
        // Refresh info
        await fetchAgenticCodeInfo();
      }
    } catch (error: any) {
      console.error("Error setting agentic code:", error);
      const errorMessage = error.response?.data?.error || "Failed to set agentic code";
      notifyError(errorMessage);
    } finally {
      setIsSubmittingCode(false);
    }
  };

  // ===================================
  // 🔐 NEW: CLEAR AGENTIC SESSION
  // ===================================
  const handleClearSession = async () => {
    if (!window.confirm("Are you sure you want to clear your agentic session? You'll need to re-authenticate for agentic actions.")) {
      return;
    }

    try {
      const response = await api.post("/user/agentic-code/clear-session");
      
      if (response.data.success) {
        notifySuccess("Agentic session cleared successfully");
        await fetchAgenticCodeInfo();
      }
    } catch (error) {
      console.error("Error clearing session:", error);
      notifyError("Failed to clear session");
    }
  };

  // ===================================
  // EXISTING PROFILE HANDLERS (Unchanged)
  // ===================================
  const handleSave = () => {
    updateProfile(formData);
    setIsEditing(false);
    notifySuccess("Your profile has been successfully updated.");
  };

  const getRoleColor = (roleLevel: number) => {
    if (roleLevel <= 1) return "bg-red-100 text-red-800";
    if (roleLevel <= 3) return "bg-blue-100 text-blue-800";
    if (roleLevel <= 6) return "bg-green-100 text-green-800";
    return "bg-[#020617] text-[#64748b]";
  };

  const permissions: string[] = Array.isArray(user?.permission)
    ? user!.permission
    : user?.permission?.split(",") || [];

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="min-h-screen bg-[#0f172a]">
      <Header
        title="Profile Settings"
        subtitle="Manage your account information"
      />

      <main className="max-w-3xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* EXISTING: Profile Card (Unchanged) */}
          <div className="card mb-6 !bg-[#1e293b] !border-[#334155] border">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-white">
                  Account Information
                </h3>
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className="btn-secondary text-sm flex items-center space-x-1"
                >
                  <Settings className="w-4 h-4" />
                  <span>{isEditing ? "Cancel" : "Edit"}</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Profile Picture */}
                <div className="md:col-span-2 flex items-center space-x-4">
                  <div className="w-16 h-16 bg-[#8b3dff] rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-white">
                      {user?.name}
                    </h4>
                    <p className="text-sm text-[#64748b]">{user?.email}</p>
                  </div>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-[#64748b] mb-1">
                    <User className="w-4 h-4 inline mr-1" />
                    Full Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      className="input-field text-white !border-[#334155] border focus:!outline-[#8b3dff] focus:ring-2 "
                      style={{ backgroundColor: "#020617" }}
                    />
                  ) : (
                    <p className="text-white">{user?.name}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-[#64748b] mb-1">
                    <Mail className="w-4 h-4 inline mr-1" />
                    Email Address
                  </label>
                  {isEditing ? (
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          email: e.target.value,
                        }))
                      }
                      className="input-field text-white !border-[#334155] border focus:!outline-[#8b3dff] focus:ring-2 "
                      style={{ backgroundColor: "#020617" }}
                    />
                  ) : (
                    <p className="text-white">{user?.email}</p>
                  )}
                </div>

                {/* Username */}
                <div>
                  <label className="block text-sm font-medium text-[#64748b] mb-1">
                    Username
                  </label>
                  <p className="text-white">{user?.user_name}</p>
                  <p className="text-xs text-[#64748b]">
                    Username cannot be changed
                  </p>
                </div>

                {/* Business */}
                <div>
                  <label className="block text-sm font-medium text-[#64748b] mb-1">
                    <Building className="w-4 h-4 inline mr-1" />
                    Business
                  </label>
                  <p className="text-white">{user?.current_business}</p>
                  <p className="text-xs text-[#64748b]">
                    Business ID: {user?.business_id}
                  </p>
                </div>

                {/* Role */}
                <div>
                  <label className="block text-sm font-medium text-[#64748b] mb-1">
                    <Shield className="w-4 h-4 inline mr-1" />
                    Role & Permissions
                  </label>
                  <div className="space-y-2">
                    <span
                      className={`px-2 py-1 text-xs rounded ${getRoleColor(
                        user?.role_level || 8
                      )}`}
                    >
                      {user?.role_name} (Level {user?.role_level})
                    </span>
                    <p className="text-xs text-[#64748b]">
                      Department: {user?.department_name || "Not assigned"}
                    </p>
                    <p className="text-xs text-[#64748b]">
                      Profile: {user?.profile_name}
                    </p>
                    {user?.is_admin && (
                      <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-800">
                        Administrator
                      </span>
                    )}
                  </div>
                </div>

                {/* Permissions */}
                <div>
                  <label className="block text-sm font-medium text-[#64748b] mb-1">
                    Permissions
                  </label>
                  <div className="flex flex-wrap gap-1">
                    {permissions.map((perm, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs bg-blue-100 text-[#8b3dff] rounded"
                      >
                        {perm.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {isEditing && (
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setIsEditing(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="bg-[#8b3dff] px-4 py-1 rounded-md text-white flex items-center space-x-1"
                  >
                    <Save className="w-4 h-4" />
                    <span>Save Changes</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ===================================
              🔐 NEW: AGENTIC ACCESS CODE SECTION
              =================================== */}
          <div className="card mb-6 !bg-[#1e293b] !border-[#334155] border">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <Lock className="w-6 h-6 text-purple-500" />
                  <h3 className="text-lg font-medium text-white">
                    Agentic Access Code
                  </h3>
                </div>
                {agenticCodeInfo?.hasCode && !showAgenticForm && (
                  <button
                    onClick={() => setShowAgenticForm(true)}
                    className="btn-secondary text-sm flex items-center space-x-1"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Change Code</span>
                  </button>
                )}
              </div>

              {loadingCodeInfo ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
                </div>
              ) : (
                <>
                  {/* Code Status */}
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center justify-between p-4 bg-[#0f172a] rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-full ${
                          agenticCodeInfo?.hasCode ? 'bg-green-500/20' : 'bg-yellow-500/20'
                        }`}>
                          {agenticCodeInfo?.hasCode ? (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-yellow-500" />
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {agenticCodeInfo?.hasCode ? 'Code Set' : 'No Code Set'}
                          </p>
                          <p className="text-sm text-[#64748b]">
                            {agenticCodeInfo?.hasCode 
                              ? 'You can perform agentic actions' 
                              : 'Set a code to enable agentic actions'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {agenticCodeInfo?.hasCode && (
                      <>
                        {/* Last Changed */}
                        <div className="flex items-center justify-between p-4 bg-[#0f172a] rounded-lg">
                          <div className="flex items-center space-x-3">
                            <Clock className="w-5 h-5 text-[#64748b]" />
                            <div>
                              <p className="text-sm text-[#64748b]">Last Changed</p>
                              <p className="text-white">
                                {formatDate(agenticCodeInfo.lastChanged)}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Active Session */}
                        <div className="flex items-center justify-between p-4 bg-[#0f172a] rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className={`p-2 rounded-full ${
                              agenticCodeInfo.hasActiveSession ? 'bg-green-500/20' : 'bg-gray-500/20'
                            }`}>
                              <Shield className={`w-5 h-5 ${
                                agenticCodeInfo.hasActiveSession ? 'text-green-500' : 'text-gray-500'
                              }`} />
                            </div>
                            <div>
                              <p className="text-white font-medium">
                                {agenticCodeInfo.hasActiveSession ? 'Active Session' : 'No Active Session'}
                              </p>
                              {agenticCodeInfo.hasActiveSession && agenticCodeInfo.sessionExpiresAt && (
                                <p className="text-sm text-[#64748b]">
                                  Expires: {formatDate(agenticCodeInfo.sessionExpiresAt)}
                                </p>
                              )}
                            </div>
                          </div>
                          {agenticCodeInfo.hasActiveSession && (
                            <button
                              onClick={handleClearSession}
                              className="text-sm text-red-400 hover:text-red-300"
                            >
                              Clear Session
                            </button>
                          )}
                        </div>

                        {/* Locked Status */}
                        {agenticCodeInfo.isLocked && (
                          <div className="flex items-center space-x-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                            <AlertCircle className="w-5 h-5 text-red-500" />
                            <div>
                              <p className="text-red-400 font-medium">Account Locked</p>
                              <p className="text-sm text-red-400/70">
                                Too many failed attempts. Try again after{' '}
                                {formatDate(agenticCodeInfo.lockedUntil)}
                              </p>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Change/Set Code Form */}
                  {(showAgenticForm || !agenticCodeInfo?.hasCode) && (
                    <form onSubmit={handleAgenticCodeSubmit} className="space-y-4">
                      <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                        <div className="flex items-start space-x-3">
                          <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5" />
                          <div>
                            <p className="text-blue-400 font-medium">Security Notice</p>
                            <p className="text-sm text-blue-400/70 mt-1">
                              Your agentic code is used to authorize automated actions.
                              Use a 6-digit code that you can remember but others cannot guess.
                            </p>
                          </div>
                        </div>
                      </div>

                      {agenticCodeInfo?.hasCode && (
                        <div>
                          <label className="block text-sm font-medium text-[#64748b] mb-2">
                            Current Code *
                          </label>
                          <div className="relative">
                            <input
                              type={showCodes.current ? "text" : "password"}
                              value={agenticFormData.currentCode}
                              onChange={(e) => setAgenticFormData({
                                ...agenticFormData,
                                currentCode: e.target.value.replace(/\D/g, '')
                              })}
                              className="w-full px-4 py-2 bg-[#0f172a] border border-[#334155] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                              placeholder="Enter current code"
                              maxLength={10}
                              required
                            />
                            <button
                              type="button"
                              onClick={() => setShowCodes({
                                ...showCodes,
                                current: !showCodes.current
                              })}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-white"
                            >
                              {showCodes.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-[#64748b] mb-2">
                          New Code * (minimum 6 digits)
                        </label>
                        <div className="relative">
                          <input
                            type={showCodes.new ? "text" : "password"}
                            value={agenticFormData.newCode}
                            onChange={(e) => setAgenticFormData({
                              ...agenticFormData,
                              newCode: e.target.value.replace(/\D/g, '')
                            })}
                            className="w-full px-4 py-2 bg-[#0f172a] border border-[#334155] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                            placeholder="Enter 6+ digit code"
                            maxLength={10}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowCodes({
                              ...showCodes,
                              new: !showCodes.new
                            })}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-white"
                          >
                            {showCodes.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-[#64748b] mb-2">
                          Confirm New Code *
                        </label>
                        <div className="relative">
                          <input
                            type={showCodes.confirm ? "text" : "password"}
                            value={agenticFormData.confirmCode}
                            onChange={(e) => setAgenticFormData({
                              ...agenticFormData,
                              confirmCode: e.target.value.replace(/\D/g, '')
                            })}
                            className="w-full px-4 py-2 bg-[#0f172a] border border-[#334155] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                            placeholder="Re-enter new code"
                            maxLength={10}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowCodes({
                              ...showCodes,
                              confirm: !showCodes.confirm
                            })}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748b] hover:text-white"
                          >
                            {showCodes.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <div className="flex space-x-3">
                        <button
                          type="submit"
                          disabled={isSubmittingCode}
                          className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSubmittingCode ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          ) : (
                            <>
                              <Save className="w-4 h-4" />
                              <span>{agenticCodeInfo?.hasCode ? 'Update Code' : 'Set Code'}</span>
                            </>
                          )}
                        </button>
                        {agenticCodeInfo?.hasCode && (
                          <button
                            type="button"
                            onClick={() => {
                              setShowAgenticForm(false);
                              setAgenticFormData({
                                currentCode: "",
                                newCode: "",
                                confirmCode: ""
                              });
                            }}
                            className="px-4 py-2 border border-[#334155] text-[#64748b] rounded-lg hover:bg-[#0f172a]"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </form>
                  )}
                </>
              )}
            </div>
          </div>

          {/* EXISTING: Activity Summary (Unchanged) */}
          <div className="card !bg-[#1e293b] !border-[#334155] border">
            <div className="p-6">
              <h3 className="text-lg font-medium text-white mb-4">
                Account Activity
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#64748b]">
                    Account created
                  </span>
                  <span className="text-sm text-white">Phase 1 POC User</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#64748b]">Last login</span>
                  <span className="text-sm text-white">Current session</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#64748b]">Access level</span>
                  <span className="text-sm text-white">
                    {user?.role_level === 0
                      ? "Full Access"
                      : user?.role_level && user.role_level <= 3
                      ? "Management Access"
                      : user?.role_level && user.role_level <= 6
                      ? "Standard Access"
                      : "Limited Access"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;